type Attributes = JSX.IntrinsicElements["div"];
type ElementFunction = ((props?: any) => HTMLElement) & {
  defaultProps?: any;
};
type Child =
  | Node
  | string
  | null
  | boolean
  | undefined
  | Element
  | Array<Child>;

export function createElement(
  type: ElementFunction | string,
  attributes?: Attributes | null,
  ...children: Array<Child>
): Element {
  const element = create(type, attributes);

  addChildren(element, children);

  if (!attributes) {
    return element;
  }

  // Set attributes
  for (let [name, value] of Object.entries(attributes)) {
    if (name === "htmlFor") {
      name = "for";
    }

    if (name === "class" || name === "className") {
      const existingClassname = element.getAttribute("class") ?? "";
      setAttribute(
        element,
        "class",
        (existingClassname + " " + String(value)).trim()
      );
    } else if (name === "style") {
      setCSSProps(element, value);
    } else if (name.startsWith("on")) {
      const eventName = name.slice(2).toLowerCase().replace(/^-/, "");
      element.addEventListener(eventName, value);
    } else if (name !== "key" && value !== false) {
      setAttribute(element, name, value === true ? "" : value);
    }
  }

  return element;
}

const create = (
  type: ElementFunction | string,
  attributes?: Attributes | null
): HTMLElement => {
  if (typeof type === "string") {
    return document.createElement(type);
  }

  return type(attributes ?? type.defaultProps);
};

const addChildren = (
  parent: Element | DocumentFragment,
  children: Array<Child>
): void => {
  for (const child of children) {
    if (child instanceof Node) {
      parent.appendChild(child);
    } else if (Array.isArray(child)) {
      addChildren(parent, child);
    } else if (
      typeof child !== "boolean" &&
      typeof child !== "undefined" &&
      child !== null
    ) {
      parent.appendChild(document.createTextNode(child));
    }
  }
};

const setAttribute = (
  element: HTMLElement,
  name: string,
  value: string
): void => {
  if (value === undefined || value === null || typeof value === "function") {
    return;
  }

  element.setAttribute(name, value);
};

// Copied from Preact
// https://github.com/preactjs/preact/blob/b5bdcefaa610178cce29ff0d109a036228ef1a2e/src/constants.js#L15-L16
const IS_NON_DIMENSIONAL =
  /acit|ex(?:s|g|n|p|$)|rph|grid|ows|mnc|ntw|ine[ch]|zoo|^ord|itera/i;

const setCSSProps = (
  element: HTMLElement,
  style: CSSStyleDeclaration
): void => {
  for (const [name, value] of Object.entries(style)) {
    if (name.startsWith("-")) {
      element.style.setProperty(name, value);
    } else if (typeof value === "number" && !IS_NON_DIMENSIONAL.test(name)) {
      element.style[name as any] = `${value as string}px`;
    } else {
      element.style[name as any] = value;
    }
  }
};

export function render(
  element: Element | ElementFunction,
  container: HTMLElement
) {
  if (container.contains(element as Node)) {
    return;
  }

  container.innerHTML = "";
  container.appendChild(element as Node);
}

export const React = {
  createElement,
};

export const h = createElement;

export default React;
